import React from "react";
import PropTypes from "prop-types";
import { Controller } from "react-hook-form";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";

const BootTextField = (props) => {
  const {
    children,
    startAdornment,
    label,
    inputValue,
    name,
    type,
    control,
    rows,
    sx,
    multiline,
    rules,
    margin,
    isRequired,
    disabled,
    changehandler,
    placeholder,
    pattern,
    readOnly,
    ref,
    bgColor,
    handleBlur,
    defaultValue,
    handleKeyDown,
    handleOnFocus,
    className,
  } = props;

  // Determine the default value based on type and props
  const getDefaultValue = () => {
    if (defaultValue !== undefined) return defaultValue;
    return type === "number" ? "" : ""; // Default to empty string for all types
  };

  const handleNumberChange = (e) => {
    const value = e.target.value;
    if (value === "") {
      // Allow empty string for number type
      changehandler?.("");
      return "";
    }
    const parsedValue = parseFloat(value);
    if (isNaN(parsedValue) && value !== "") {
      // Invalid number, keep the last valid value or empty string
      return field.value || "";
    }
    changehandler?.(parsedValue);
    return parsedValue;
  };

  // Sanitize input to remove double and single quotes
  const sanitizeInput = (value) => {
    return value.replace(/['"]/g, ""); // Remove both single and double quotes
  };

  return control ? (
    <Controller
      control={control}
      name={name}
      defaultValue={getDefaultValue()}
      render={({ field, fieldState }) => (
        <Form.Group className={`${margin || ""}`} controlId={name}>
          {label && (
            <Form.Label>
              {label} {isRequired && <span className="text-danger">*</span>}
            </Form.Label>
          )}

          <InputGroup hasValidation>
            {startAdornment && (
              <InputGroup.Text>{startAdornment}</InputGroup.Text>
            )}

            <Form.Control
              as={multiline ? "textarea" : "input"}
              rows={rows}
              type={type}
              placeholder={placeholder}
              disabled={disabled}
              readOnly={readOnly}
              isInvalid={!!fieldState.error}
              value={inputValue !== undefined ? inputValue : field.value}
              className={className}
              style={{
                backgroundColor: bgColor,
                border: fieldState.error ? "1px solid #dc3545" : undefined,
                ...sx,
              }}
              pattern={pattern}
              {...field}
              onChange={(e) => {
                const rawValue = e.target.value;
                const sanitizedValue = sanitizeInput(rawValue);
                const newValue =
                  type === "number"
                    ? handleNumberChange({ target: { value: sanitizedValue } })
                    : sanitizedValue;
                field.onChange(newValue);
                changehandler?.(newValue);
              }}
              onBlur={(e) => {
                field.onBlur();
                handleBlur?.(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              onFocus={handleOnFocus}
              onWheel={(e) => type === "number" && e.target.blur()} // Prevent scrolling number changes
              autoComplete="off"
            />

            {children && <InputGroup.Text>{children}</InputGroup.Text>}
          </InputGroup>

          {fieldState.error && (
            <Form.Control.Feedback type="invalid">
              {fieldState.error.message}
            </Form.Control.Feedback>
          )}
        </Form.Group>
      )}
      rules={rules}
    />
  ) : (
    <Form.Group className={`${margin || ""}`} controlId={name}>
      {label && (
        <Form.Label>
          {label} {isRequired && <span className="text-danger">*</span>}
        </Form.Label>
      )}

      <InputGroup>
        {startAdornment && <InputGroup.Text>{startAdornment}</InputGroup.Text>}

        <Form.Control
          as={multiline ? "textarea" : "input"}
          rows={rows}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          ref={ref}
          value={inputValue || ""}
          className={className}
          onChange={(e) => {
            const rawValue = e.target.value;
            const sanitizedValue = sanitizeInput(rawValue);
            const newValue =
              type === "number"
                ? handleNumberChange({ target: { value: sanitizedValue } })
                : sanitizedValue;
            changehandler?.(newValue);
          }}
          onBlur={(e) => handleBlur?.(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={handleOnFocus}
          onWheel={(e) => type === "number" && e.target.blur()} // Prevent scrolling number changes
          autoComplete="off"
        />

        {children && <InputGroup.Text>{children}</InputGroup.Text>}
      </InputGroup>
    </Form.Group>
  );
};

BootTextField.propTypes = {
  isRequired: PropTypes.bool,
  disabled: PropTypes.bool,
  changehandler: PropTypes.func,
  bgColor: PropTypes.string,
  handleBlur: PropTypes.func,
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  type: PropTypes.string,
  control: PropTypes.object,
  rules: PropTypes.object,
  className: PropTypes.string,
  startAdornment: PropTypes.node,
  children: PropTypes.node,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  placeholder: PropTypes.string,
  pattern: PropTypes.string,
  readOnly: PropTypes.bool,
  inputValue: PropTypes.any,
  margin: PropTypes.string,
  handleKeyDown: PropTypes.func,
  handleOnFocus: PropTypes.func,
  defaultValue: PropTypes.any,
};

BootTextField.defaultProps = {
  isRequired: false,
  disabled: false,
  changehandler: () => null,
  handleBlur: () => null,
  type: "text",
  multiline: false,
  rows: 3,
  className: "",
  margin: "mb-3",
};

export default BootTextField;
